/* [04]-STUDIOS™ — Local cart memory, V3 draft.
   Keeps only a recent snapshot on this browser. No automatic cart changes.
   Shopify remains the source of truth for live stock and checkout prices. */
(() => {
  'use strict';
  if (window.__cart04MemoryLoaded) return;
  window.__cart04MemoryLoaded = true;

  const KEY = '04-studios:local-cart-memory:v1';
  const MAX_ITEMS = 8;
  const MAX_AGE = 90 * 24 * 60 * 60 * 1000;
  const noop = () => {};
  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  function validSavedItem(item) {
    return item && Number.isSafeInteger(Number(item.variantId)) && Number(item.variantId) > 0 &&
      typeof item.title === 'string' && item.title.length < 320 &&
      Number.isFinite(Number(item.quantity)) && Number(item.quantity) >= 1;
  }
  function readSaved() {
    try {
      const data = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (!data || !Array.isArray(data.items) || !Number.isFinite(data.savedAt) ||
          Date.now() - data.savedAt > MAX_AGE || data.savedAt > Date.now() + 3600000) return [];
      return data.items.filter(validSavedItem).slice(0, MAX_ITEMS);
    } catch { return []; }
  }
  function remember(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;
    const items = cart.items.filter(item =>
      item && Number(item.variant_id) > 0 && typeof item.product_title === 'string'
    ).slice(0, MAX_ITEMS).map(item => ({
      variantId: Number(item.variant_id),
      quantity: Math.max(1, Math.min(20, Number(item.quantity) || 1)),
      title: item.product_title.slice(0, 260),
      variant: typeof item.variant_title === 'string' && item.variant_title !== 'Default Title'
        ? item.variant_title.slice(0, 100) : '',
      url: typeof item.url === 'string' && item.url.startsWith('/products/') ? item.url : '',
      image: typeof item.image === 'string' && (
        item.image.startsWith('https://cdn.shopify.com/') ||
        item.image.startsWith('//cdn.shopify.com/') ||
        item.image.startsWith('/cdn/')
      ) ? item.image : ''
    }));
    if (!items.length) return;
    try { localStorage.setItem(KEY, JSON.stringify({savedAt:Date.now(),items})); } catch {}
  }

  function create(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }
  function renderSaved() {
    const items = readSaved();
    for (const container of document.querySelectorAll('[data-cart04-memory]')) {
      const signature = JSON.stringify(items);
      if (container.dataset.cart04Render === signature) continue;
      container.dataset.cart04Render = signature;
      container.hidden = items.length === 0;
      const list = container.querySelector('[data-cart04-memory-list]');
      if (!list || !items.length) continue;
      list.replaceChildren();
      for (const item of items) {
        const li = create('li','cart04-memory__item');
        if (item.image) {
          const img = create('img','cart04-memory__item-image');
          img.src = item.image;
          img.loading = 'lazy';
          img.alt = '';
          li.append(img);
        } else li.append(create('span','cart04-memory__item-placeholder'));
        const copy = create('div','cart04-memory__item-copy');
        const name = create(item.url ? 'a' : 'span','cart04-memory__item-name',item.title);
        if (item.url) name.href = item.url;
        copy.append(name);
        const details = [item.variant, 'Qty ' + item.quantity].filter(Boolean).join(' · ');
        copy.append(create('div','cart04-memory__item-detail',details));
        li.append(copy);
        list.append(li);
      }
    }
  }

  let refreshTimeout = 0;
  function refreshSoon(delay = 180) {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(refreshCart, delay);
  }
  let pendingFetch = null;
  async function refreshCart() {
    if (pendingFetch) return pendingFetch;
    pendingFetch = (async () => {
      try {
        const response = await fetch(root() + 'cart.js', {
          credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}
        });
        if (!response.ok) return;
        remember(await response.json());
      } catch {}
      finally { renderSaved(); pendingFetch = null; }
    })();
    return pendingFetch;
  }

  let restoring = false;
  async function restore(button) {
    if (restoring) return;
    const box = button.closest('[data-cart04-memory]');
    const status = box?.querySelector('[data-cart04-memory-status]');
    const items = readSaved();
    if (!items.length) { renderSaved(); return; }
    restoring = true;
    button.disabled = true;
    const restoreLabel = button.querySelector('[data-cart04-memory-restore-label]');
    if (restoreLabel) restoreLabel.textContent = 'Restoring…';
    if (status) status.textContent = '';
    let restored = 0, failed = 0;
    try {
      // Work one line at a time so an unavailable product cannot block others.
      for (const item of items) {
        try {
          const response = await fetch(root() + 'cart/add.js', {
            method:'POST', credentials:'same-origin',
            headers:{'Content-Type':'application/json',Accept:'application/json'},
            body:JSON.stringify({items:[{id:item.variantId,quantity:item.quantity}]})
          });
          if (response.ok) restored++; else failed++;
        } catch { failed++; }
      }
      if (restored) {
        if (status) status.textContent = failed
          ? 'Some items were unavailable. Opening your updated cart…'
          : 'Items restored. Opening your cart…';
        await refreshCart();
        // Preserve a draft theme preview when a merchant tests recovery.
        const cartUrl = new URL(root() + 'cart', window.location.origin);
        const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewTheme && /^\d{8,20}$/.test(previewTheme)) {
          cartUrl.searchParams.set('preview_theme_id', previewTheme);
        }
        window.location.assign(cartUrl.pathname + cartUrl.search);
      } else if (status) {
        status.textContent = 'Those items could not be restored. They may be sold out or no longer available.';
      }
    } finally {
      restoring = false;
      button.disabled = false;
      if (restoreLabel) restoreLabel.textContent = 'Restore saved items';
    }
  }

  function onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-cart04-memory-restore]');
    if (button) {
      event.preventDefault();
      restore(button);
      return;
    }
    // Cross-app/cart quick-add fallback: persist once Shopify's AJAX
    // action has had time to finish if a connector omitted the cart event.
    if (target.closest('[name="add"],[data-pdpref-buy],[data-add-to-cart],[data-quick-add]')) {
      refreshSoon(1800);
    }
  }
  function onCartUpdate(event) {
    if (event?.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => refreshSoon(100), noop);
    } else refreshSoon(700);
  }
  function start() {
    renderSaved();
    document.addEventListener('click', onClick);
    document.addEventListener('cart:updated', () => refreshSoon(300));
    document.addEventListener('cart:refresh', () => refreshSoon(300));
    document.addEventListener('cart:change', () => refreshSoon(300));
    document.addEventListener('cart:updated-04', () => refreshSoon(300));
    document.addEventListener('cart:lines-update', onCartUpdate);
    document.addEventListener('cart:lines:update', onCartUpdate);
    document.addEventListener('cart:line-items:updated', onCartUpdate);
    document.addEventListener('cartLinesUpdate', onCartUpdate);
    window.addEventListener('pageshow', () => { renderSaved();refreshSoon(250); });
    window.addEventListener('storage', (event) => { if(event.key === KEY) renderSaved(); });
    const observer = new MutationObserver(() => { if(!document.hidden) renderSaved(); });
    const targets = [document.getElementById('shopify-section-cart-drawer-section'),
      document.getElementById('MainContent')].filter(Boolean);
    for (const target of targets) observer.observe(target,{subtree:true,childList:true});
    // Delay network access until the shop has rendered its critical content.
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => refreshSoon(0),{timeout:1600});
    else refreshSoon(1100);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true});
  else start();
})();
